/**
 * Tatting patterns — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_stitch_charts`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const tattingPatternsPack: ToilePack = {
    slug: 'tatting-patterns',
    category: 'craft',
    name: 'Tatting patterns',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'edging', label: 'Edging', subject: 'a tatting diagram for a lace edging, a row of rings joined by arched chains, small picot loops round each ring and the stitch counts written beside each section'},
        {key: 'doily', label: 'Doily', subject: 'a tatting diagram of a small round doily, a centre ring with eight picots and a round of rings and chains joined to them, drawn as clear outlines'},
        {key: 'snowflake', label: 'Snowflake', subject: 'a tatting diagram of a six-pointed snowflake, clover-leaf groups of three rings at each point and chains linking them to a central ring'},
    ],
    styles: [
        {key: 'outline', label: 'Thin outline', style: 'thin even black outlines for rings and chains, picots as small loops and joins marked by small dots', palette: 'black ink only on pure white', framing: 'centered on the sheet with an even white margin all round'},
        {key: 'bold', label: 'Bold tracing', style: 'thick even outlines easy to follow with a finger, picots as clear loops and joins as filled dots', palette: 'black ink only on pure white', framing: 'centered on the sheet with an even white margin all round'},
        {key: 'thread', label: 'Two threads', style: 'rings in black outline and chains in a second colour of line, picots as small loops', palette: 'black and one coloured line on pure white', framing: 'centered on the sheet with an even white margin all round'},
    ],
    directions: ['earrings', 'bookmark', 'butterfly'],
    directionHint: 'try: earrings',
    seo: {
        title: 'Tatting patterns: edging, doily, snowflake',
        metaDescription: 'Nine tatting diagrams: a lace edging, a round doily and a snowflake, with rings, chains, picots and joins in outline.',
        h1: 'Tatting patterns: a lace edging, a doily and a snowflake',
        tagline: 'Rings and chains drawn as outlines, with the picots as small loops and the joins marked.',
        intro: 'Tatting makes lace from rings and chains of knots, for edgings, doilies, bookmarks and earrings. A diagram draws rings and chains as outlines, with picots as small loops and joins marked. These nine cover a lace edging, a round doily and a six-pointed snowflake. Each comes in thin outline, bold tracing line, or two threads. Type a different piece and the same diagram layout carries it.',
        useCases: [
            'a lace edging',
            'a bookmark',
            'a pair of earrings',
            'a small doily',
        ],
        faq: [
            {q: 'Is a tatting diagram the same as a written pattern?', a: 'No. The diagram shows the rings, chains and joins as outlines. The stitch counts are written beside each section on the edging diagram.'},
            {q: 'What are the small loops?', a: 'The picots, small loops that stand out along a ring or chain.'},
            {q: 'Which style is easiest to follow with a finger?', a: 'The bold tracing style, with thick even outlines and joins marked as filled dots.'},
        ],
    },
}
